// ONLY WHAT YOU KNOW — the short list of blanks nobody but the sender
// can answer.
//
// CONSUMER: shared (web prep screen + node-pool tests).
//
// A reviewed contract has hundreds of blanks. The AAR purchase contract
// has 324. Of those, the overwhelming majority are things the OS
// already holds (the buyer's name, the property, the parcel number),
// things the firm has one standard answer for (the response window, the
// compensation rate), or signatures. What is left — the price, the
// earnest money, the close date, the fixtures — is about fourteen
// questions, and they are the entire reason the sender is at the
// keyboard.
//
// Until now the prep screen opened all 324 as identical empty boxes on
// a page, and finding the fourteen was the job. The fill rules recorded
// on each blank (fill-rules.ts, 2026-09-15) are what make the
// difference visible, so this turns them into a short form.
//
// WHAT IS ASKED, AND WHAT IS DELIBERATELY NOT
//
//   preparer  ASKED. This is the definition of the list.
//   gap       ASKED, and marked. The OS has no home for this value, so
//             nobody can fill it but the sender — and saying so is
//             better than an unexplained empty box.
//   default   SHOWN, already answered, editable. The firm's standard
//             answer is right almost every time and wrong occasionally;
//             hiding it would make "occasionally" impossible to catch,
//             and asking for it would waste the sender's attention on
//             the answer they were going to accept.
//   record / feed
//             NOT asked ONLY when the blank carries a merge token. The
//             rule says a named column fills this; a token is what makes
//             that true. This line used to say "NOT asked" flatly, for
//             the same reason the derive line below did, and it was
//             wrong the same way: measured 2026-09-18, 203 of the
//             atlas's 261 `record` blanks carried no token. They printed
//             nothing, were never on this list, and were counted as
//             filled. An unbound one is now asked, as a gap — the
//             sender is covering for a value the OS was meant to hold,
//             and naming it a gap is what keeps that work visible.
//   intake    NOT asked ONLY when an intake form actually asks the
//             client. 669 rows in the atlas, no form wired to one of
//             them. Unlike the two above, these do NOT become the
//             sender's job: "does the roof leak" is not Nate's answer to
//             give, and putting the seller's 276 disclosures on his
//             question list would be a worse lie than the one it
//             replaced. They are listed as the client's, and counted as
//             neither filled nor asked. See intake-wiring.ts.
//   derive    NOT asked ONLY when the named calculation exists. This
//             line used to say "NOT asked" flatly, and that was the
//             defect: measured 2026-09-16, the Realty atlas carried 87
//             derive rows, 68 reached built layouts, and not one derive
//             rule was implemented anywhere. So 68 blanks — the counter
//             offer's own number, which document it counters, whether
//             the buyer or the seller is countering, all twelve "Addenda
//             Incorporated" boxes on the purchase contract — went out
//             empty, were never on this list, and were counted in the
//             "the OS filled N" line the screen leads with. An
//             unimplemented rule is now asked, as a gap. See
//             derive-rules.ts.
//   sign      NOT asked. Nobody types a signature.
//
// Pure and dependency-free, so the decision about what a person is made
// to answer is testable on its own.

import type { EsignFieldType } from "./field-types";
import { deriveRuleIsImplemented } from "./derive-rules";
import { intakeTargetIsWired } from "./intake-wiring";
import { parseFillRule, type FillKind } from "./fill-rules";

/** One placed blank, as the prep screen holds it. */
export interface AskableField {
  /** The field's own handle, so an answer can be written back to it. */
  key: string;
  documentId: string;
  pageNumber: number;
  fieldType: EsignFieldType;
  /** Position down the page, for ordering questions the way the form
   *  reads rather than the way the fields happen to be stored. */
  y: number;
  fillRule?: string | null;
  /** The words printed beside this blank on the form. What makes a
   *  question answerable: "Full purchase price" is a question, "field 12
   *  on page 1" is a puzzle. */
  caption?: string | null;
  value?: string | null;
  /** What this blank is BOUND to, when it is bound to anything.
   *
   *  A `record` rule says "the OS fills this from a named column." That
   *  is true only when a merge token carries it; an unbound one prints
   *  nothing. Without this field the list could not tell those apart,
   *  which is how 203 of 261 `record` blanks came to be counted as
   *  filled while going out empty (2026-09-18). */
  mergeToken?: string | null;
}

/** What the sender is being told about a blank.
 *
 *  `ask` and `gap` are theirs to answer. `default` is answered already
 *  and editable. `client` is somebody else's answer entirely — see
 *  intake-wiring.ts for why that is a third kind rather than a fourth
 *  way of saying "asked". */
export type QuestionKind = "ask" | "gap" | "default" | "client";

export interface Question {
  /** The field this answer belongs to. */
  key: string;
  /** What to call it, from the form's own words. */
  label: string;
  /** Where it lands, so the sender can check it against the page. */
  pageNumber: number;
  kind: QuestionKind;
  fieldType: EsignFieldType;
  /** Already filled for a `default`; whatever has been typed otherwise. */
  value: string | null;
}

export interface QuestionGroup {
  documentId: string;
  documentName: string;
  questions: Question[];
}

export interface OnlyWhatYouKnow {
  groups: QuestionGroup[];
  /** How many the sender is actually being asked (ask + gap). The number
   *  the screen leads with, because it is the size of the job. */
  askedCount: number;
  /** How many carry a standard answer already. */
  defaultedCount: number;
  /** Blanks waiting on the CLIENT that no intake form asks them yet, so
   *  nobody is going to fill them. Not the sender's job and not filled:
   *  its own number precisely because it belongs in neither of the
   *  other two. */
  waitingOnClientCount: number;
  /** How many blanks the OS answered on its own, across these
   *  documents. Counted for the sentence that makes the short list
   *  believable: "14 questions; the OS filled 219." */
  filledForYouCount: number;
  /** True when at least one document carries no fill rules at all, so
   *  the screen can say why its list looks short rather than implying
   *  there is nothing to answer. */
  someDocumentsUnmapped: boolean;
}

const NEVER_ASKED: readonly EsignFieldType[] = ["signature", "initials", "date_signed"];

/** What each kind resolves to WHEN ITS PROMISE IS KEPT. Null means the
 *  blank really does fill itself, so the sender never sees it.
 *
 *  Three of these are conditional, and the condition is checked below
 *  rather than here, because it depends on the field rather than on its
 *  kind: a `record` or `feed` blank keeps its promise only when it
 *  carries a merge token, and an `intake` blank only when a form
 *  actually asks the client. */
const ASKED_BY_KIND: Readonly<Record<FillKind, QuestionKind | null>> = {
  preparer: "ask",
  gap: "gap",
  default: "default",
  record: null,
  derive: null,
  feed: null,
  intake: null,
  sign: null,
};

/** What the sender is shown for a blank, once the promises are checked.
 *
 *  Every branch here exists because the same defect was found three
 *  times in three kinds: a fill rule that names a source, with nothing
 *  behind it, counted as handled and never mentioned. Fixed for `derive`
 *  on 2026-09-16 (68 blanks), and for these two on 2026-09-18 (203
 *  `record`, 669 `intake`).
 *
 *  The test each one applies is the same: would a person, looking at the
 *  finished document, find something printed here? If not, the sender
 *  hears about it. */
function questionKindFor(
  rule: { kind: FillKind; ref: string | null },
  field: AskableField,
): QuestionKind | null {
  switch (rule.kind) {
    // A named calculation over OS data, kept only when the calculation
    // exists. An unkept one is asked, exactly like a gap: to the sender
    // they are the same thing.
    case "derive":
      return deriveRuleIsImplemented(rule.ref, field) ? null : "gap";

    // "Filled from a named OS column" and "read from an outside source
    // at package start" are the same promise wearing two labels, and a
    // merge token is what keeps both. Without one the box prints
    // nothing, and nothing downstream can fill it — there is no signer
    // to ask and no column bound to read.
    //
    // Asked as a `gap` rather than an `ask`: the sender is not being
    // asked for their judgement here, they are covering for a value the
    // OS was supposed to hold. Naming it that way is what makes the
    // work visible instead of looking like an ordinary question.
    //
    // One more way the promise is kept (session 4, 2026-11-04): the
    // atlas filed the offer's PARTS as `record` rows ("offer.loan_type →
    // transactions.loan_type", a checkbox; "offer.close_of_escrow_date
    // (month)"). No token can carry a part, so they stayed gaps for as
    // long as a token was the only way to fill a box. The binder now
    // works them out from the typed offer, so a `record` row a derive
    // rule claims is filled, token or not.
    case "record":
    case "feed":
      if (field.mergeToken && field.mergeToken.trim().length > 0) return null;
      return deriveRuleIsImplemented(rule.ref, field) ? null : "gap";

    // The sender's own blank, unless the binder composes it for them
    // (lines 12 to 16 of the purchase contract, built from the financing
    // terms). The rule stays `preparer` on the map because the words are
    // theirs to change; the question list stops asking for it.
    case "preparer":
      return deriveRuleIsImplemented(rule.ref, field) ? null : "ask";

    // The client's own answer. Kept only when a form actually asks them
    // — and when it does not, this is deliberately NOT the sender's
    // problem. See intake-wiring.ts: putting the seller's 276
    // disclosures on Nate's question list would be a worse lie than the
    // one it replaced.
    case "intake":
      return intakeTargetIsWired(rule.ref) ? null : "client";

    default:
      return ASKED_BY_KIND[rule.kind];
  }
}

export function onlyWhatYouKnow(
  fields: readonly AskableField[],
  documentNameById: ReadonlyMap<string, string>,
): OnlyWhatYouKnow {
  const byDocument = new Map<string, Question[]>();
  let filledForYouCount = 0;
  const documentsWithRules = new Set<string>();
  const documentsSeen = new Set<string>();

  for (const f of fields) {
    documentsSeen.add(f.documentId);
    if (NEVER_ASKED.includes(f.fieldType)) continue;
    const rule = parseFillRule(f.fillRule);
    if (!rule) continue;
    documentsWithRules.add(f.documentId);
    const kind = questionKindFor(rule, f);
    if (kind === null) {
      filledForYouCount += 1;
      continue;
    }
    const list = byDocument.get(f.documentId) ?? [];
    list.push({
      key: f.key,
      label: labelFor(f),
      pageNumber: f.pageNumber,
      kind,
      fieldType: f.fieldType,
      value: f.value ?? null,
    });
    byDocument.set(f.documentId, list);
  }

  const groups: QuestionGroup[] = [];
  for (const [documentId, questions] of byDocument) {
    // The order the form reads: down the page, page by page. A list
    // that jumps around the document is one the sender cannot check
    // against the paper in front of them.
    questions.sort((a, b) => a.pageNumber - b.pageNumber);
    groups.push({
      documentId,
      documentName: documentNameById.get(documentId) ?? "This document",
      questions,
    });
  }

  // Counted from the questions themselves rather than tallied while
  // building them, so the numbers the screen leads with and the list it
  // shows can never describe different populations. That is the same
  // rule CLAUDE.md's accuracy section states for a total measured off a
  // capped list, applied to three totals over one array.
  let askedCount = 0;
  let defaultedCount = 0;
  let waitingOnClientCount = 0;
  for (const g of groups) {
    for (const q of g.questions) {
      if (q.kind === "default") defaultedCount += 1;
      else if (q.kind === "client") waitingOnClientCount += 1;
      else askedCount += 1;
    }
  }

  return {
    groups,
    waitingOnClientCount,
    askedCount,
    defaultedCount,
    filledForYouCount,
    someDocumentsUnmapped: [...documentsSeen].some((id) => !documentsWithRules.has(id)),
  };
}

/** The form's own words, tidied just enough to read as a question.
 *
 *  Deliberately NOT rewritten into a nicer question. The caption is what
 *  is printed beside the blank on the paper the client will sign, and
 *  the sender is checking one against the other — an improved wording
 *  would break exactly that. */
function labelFor(f: AskableField): string {
  const caption = f.caption?.trim();
  if (caption) {
    // Trailing colons and the underscores a form draws its rule with
    // are punctuation, not words.
    const tidy = caption.replace(/[_\s]+$/g, "").replace(/:$/, "").trim();
    if (tidy) return tidy.slice(0, 120);
  }
  return `Page ${f.pageNumber}`;
}

/** Apply the answers back onto the fields. Returns a new list; a field
 *  with no answer is untouched, which is what makes "leave it blank" a
 *  real choice rather than an accident. */
export function answersOntoFields<T extends { key: string; value?: string | null }>(
  fields: readonly T[],
  answers: ReadonlyMap<string, string>,
): T[] {
  return fields.map((f) => {
    if (!answers.has(f.key)) return f;
    const answer = answers.get(f.key) ?? "";
    return { ...f, value: answer === "" ? null : answer };
  });
}
